/**
 * NotificationsSection — the call to action: how not to miss the next report.
 *
 * Split out of the control block deliberately. Stating a checkpoint date and
 * getting the reader to subscribe to it are different asks, and the review
 * call listed them as separate stages of the journey. Folded together, the
 * subscription reads as a footnote to the date and gets skipped.
 *
 * The original demo bet everything on the Telegram bot, so anyone who
 * doesn't use Telegram simply never came back. Here each channel is its own
 * card with its own state, which also makes the "neither is on" case
 * impossible to miss.
 *
 * The topics list earns the ask: it says what the subscription is actually
 * for before requesting it. The promo sits last — by that point the reader
 * has seen what the monitoring found, which is the only moment the pitch is
 * earned.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Icon} from 'ui/components/atoms/Icon';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {NOTIFICATION_TOPICS, type NotificationTopic} from '../content';
import type {NotificationsData} from '../types';
import {ReportCard} from './ReportCard';

export interface NotificationsSectionProps {
  notifications: NotificationsData;
}

const Lead = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurface'),
  maxWidth: 720,
});

const ChannelName = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
});

const ChannelBody = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
});

const TopicTitle = styled(Box)({
  ...text('label.m'),
  color: surfaceVar('onSurface'),
});

const TopicBody = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

const PromoTitle = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
});

const PromoBody = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: 640,
});

const NoShrink = styled(Box)({
  flex: 'none',
});

const Channel = styled(ReportCard)({
  flex: '1 1 320px',
  minWidth: 280,
});

const Topic = styled(ReportCard)({
  flex: '1 1 260px',
  minWidth: 240,
});

interface ChannelCardProps {
  icon: string;
  name: string;
  description: string;
  connected: boolean;
  action: string;
  onAction?: () => void;
}

function ChannelCard({icon, name, description, connected, action, onAction}: ChannelCardProps) {
  return (
    <Channel accent={connected ? 'good' : 'alert'}>
      <Stack gap='s'>
        <Row gap='s' alignY='center' justify='between'>
          <Row gap='s' alignY='center'>
            <Icon name={icon} size='m' color={connected ? 'good' : 'on-surface-muted'} />
            <ChannelName>{name}</ChannelName>
          </Row>
          <ChipMUI mode='badge' color={connected ? 'good' : 'alert'} variant='soft' size='s'>
            {connected ? 'подключено' : 'не подключено'}
          </ChipMUI>
        </Row>
        <ChannelBody>{description}</ChannelBody>
        <Row gap='s'>
          <Button
            size='m'
            variant={connected ? 'ghost' : 'solid'}
            color={connected ? 'neutral' : 'primary'}
            leftIcon={icon}
            onClick={onAction}
          >
            {connected ? 'Изменить' : action}
          </Button>
        </Row>
      </Stack>
    </Channel>
  );
}

function TopicCard({topic}: {topic: NotificationTopic}) {
  return (
    <Topic>
      <Stack gap='xs'>
        <Icon name={topic.icon} size='m' color='primary' />
        <TopicTitle>{topic.title}</TopicTitle>
        <TopicBody>{topic.description}</TopicBody>
      </Stack>
    </Topic>
  );
}

export function NotificationsSection({notifications}: NotificationsSectionProps) {
  const configured = notifications.email || notifications.telegram;

  return (
    <Stack gap='m'>
      <Lead>
        {configured
          ? 'Вы получите напоминание о контрольной точке и сообщения о том, что изменится в кабинете до неё.'
          : 'Ни один канал не подключён — о следующем отчёте и о новых критичных ошибках вы не узнаете. Выберите, куда писать.'}
      </Lead>

      <Row gap='s' wrap='wrap' alignY='stretch'>
        <ChannelCard
          icon='mail'
          name='Почта'
          description='Отчёт приходит письмом со ссылкой. Удобно переслать команде или сохранить.'
          connected={notifications.email}
          action='Настроить почту'
        />
        <ChannelCard
          icon='send'
          name='Telegram'
          description='Короткие уведомления в бот: контрольная точка, новые критичные ошибки, изменение оценки.'
          connected={notifications.telegram}
          action='Подключить Telegram'
          onAction={() => window.open(notifications.telegramUrl, '_blank', 'noopener')}
        />
      </Row>

      <Row gap='s' wrap='wrap' alignY='stretch'>
        {NOTIFICATION_TOPICS.map(topic => (
          <TopicCard key={topic.title} topic={topic} />
        ))}
      </Row>

      <ReportCard padding='l'>
        <Row gap='m' alignY='center' justify='between' reflowAt='md'>
          {/* Заголовок и абзац под ним — одна мысль: разрыв между ними
              минимальный, иначе абзац читается как отдельный блок. */}
          <Stack gap='xxs'>
            <PromoTitle>Отчёт — это срез. Контроль — это система</PromoTitle>
            <PromoBody>
              AdSensor следит за кабинетом каждый день: 110 автоматических проверок находят ошибки в
              момент появления, а не спустя три месяца. Вы видите, что команда исправила, что
              осталось и как это влияет на цену обращения.
            </PromoBody>
          </Stack>
          {/* Кнопка не сжимается: флекс по умолчанию урезает её до
              min-content и режет подпись. */}
          <NoShrink>
            <Button size='m' variant='solid' color='good' rightIcon='arrow_forward'>
              Смотреть кабинет в AdSensor
            </Button>
          </NoShrink>
        </Row>
      </ReportCard>
    </Stack>
  );
}
